import React, { useState, useEffect, useContext, useMemo, Fragment } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  Icon,
  useSidebarNav
} from '@aidkitorg/component-library';
import { IconName } from '@aidkitorg/component-library/dist/types/src/Icon';
import { Text } from '@aidkitorg/types/lib/survey';
import InterfaceContext from '../Context';
import { useLocalizedStrings, langToWord } from '../Localization';
import { useTheme } from '../Contexts/ThemeContext';

type CommandAction = {
  id: string;
  label: string;
  description?: string;
  href?: string;
  iconName?: IconName;
  shortcut?: string;
  category: 'navigation' | 'actions' | 'recent';
  searchTerms?: string[];
  isExternal?: boolean;
  priority?: number; // Higher priority = shown by default
};

interface AdminCommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AdminCommandPalette: React.FC<AdminCommandPaletteProps> = ({
  isOpen,
  onClose
}) => {
  const [search, setSearch] = useState('');
  const history = useHistory();
  const L = useLocalizedStrings();
  const interfaceContext = useContext(InterfaceContext);
  const { toggleSidebarNav } = useSidebarNav();
  const { languages } = useTheme();

  // Simple fuzzy search function
  const fuzzyMatch = (text: string, query: string): { isMatch: boolean; score: number } => {
    if (!text || !query) return { isMatch: false, score: 0 };
    
    text = text.toLowerCase();
    query = query.toLowerCase();
    
    // Exact match gets highest score
    if (text.includes(query)) {
      return { isMatch: true, score: 100 };
    }
    
    // Check if all characters in query exist in text (in order)
    let textIndex = 0;
    let matchedChars = 0;
    
    for (let i = 0; i < query.length; i++) {
      const char = query[i];
      const foundIndex = text.indexOf(char, textIndex);
      
      if (foundIndex !== -1) {
        matchedChars++;
        textIndex = foundIndex + 1;
      }
    }
    
    // Calculate fuzzy score based on character matches and text length
    const matchRatio = matchedChars / query.length;
    const lengthPenalty = Math.max(0, 1 - (text.length - query.length) / text.length);
    const score = matchRatio * 80 * (0.5 + lengthPenalty * 0.5);
    
    // Consider it a match if more than 70% of characters match
    return { isMatch: matchRatio > 0.7, score };
  };

  // All available commands - registered from navigation + additional actions
  const allCommands: CommandAction[] = useMemo(() => [
    // Navigation commands from NAV_ITEMS
    {
      id: 'nav-overview',
      label: L.admin_panel.nav.dashboard_home,
      href: '/admin-panel/overview',
      iconName: 'HomeIcon',
      category: 'navigation',
      priority: 10,
      searchTerms: L.admin_panel.command_palette.search_terms.navigation.overview.split(',')
    },
    {
      id: 'nav-dashboards',
      label: L.admin_panel.nav.dashboards,
      href: '/admin-panel/dashboards',
      iconName: 'PresentationChartBarIcon',
      category: 'navigation',
      priority: 9,
      searchTerms: L.admin_panel.command_palette.search_terms.navigation.dashboards.split(','),
      description: L.admin_panel.command_palette.navigation_descriptions.dashboards
    },
    {
      id: 'nav-reports',
      label: L.admin_panel.reports.reports,
      href: '/admin-panel/reports',
      iconName: 'TableCellsIcon',
      category: 'navigation',
      priority: 8,
      searchTerms: L.admin_panel.command_palette.search_terms.navigation.reports.split(','),
      description: L.admin_panel.command_palette.navigation_descriptions.reports
    },
    {
      id: 'nav-payments',
      label: L.admin_panel.nav.payments,
      href: '/admin-panel/payments',
      iconName: 'CurrencyDollarIcon',
      category: 'navigation',
      priority: 7,
      searchTerms: L.admin_panel.command_palette.search_terms.navigation.payments.split(','),
      description: L.admin_panel.command_palette.navigation_descriptions.payments
    },
    {
      id: 'nav-accounts',
      label: L.admin_panel.nav.accounts,
      href: '/admin-panel/accounts',
      iconName: 'WalletIcon',
      category: 'navigation',
      priority: 6,
      searchTerms: L.admin_panel.command_palette.search_terms.navigation.accounts.split(','),
      description: L.admin_panel.command_palette.navigation_descriptions.accounts
    },
    {
      id: 'nav-settings',
      label: L.admin_panel.nav.settings,
      href: '/admin-panel/settings',
      iconName: 'CogIcon',
      category: 'navigation',
      priority: 5,
      searchTerms: L.admin_panel.command_palette.search_terms.navigation.settings.split(',')
    },
    {
      id: 'nav-manage-fields',
      label: L.admin_panel.settings.manage_fields,
      href: '/admin-panel/settings/manageFields',
      iconName: 'CogIcon',
      category: 'navigation',
      priority: 4,
      searchTerms: L.admin_panel.command_palette.search_terms.navigation.manage_fields.split(','),
      description: L.admin_panel.command_palette.navigation_descriptions.manage_fields
    },
    {
      id: 'nav-documentation',
      label: L.admin_panel.nav.documentation,
      href: 'https://community.aidkit.cloud/',
      iconName: 'BookOpenIcon',
      category: 'navigation',
      priority: 3,
      isExternal: true,
      searchTerms: L.admin_panel.command_palette.search_terms.navigation.documentation.split(',')
    },
    // Additional actions
    {
      id: 'action-create-report',
      label: L.admin_panel.command_palette.actions.create_report.label,
      description: L.admin_panel.command_palette.actions.create_report.description,
      href: '/admin-panel/reports/new',
      iconName: 'PlusIcon',
      category: 'actions',
      priority: 8,
      searchTerms: L.admin_panel.command_palette.search_terms.actions.create_report.split(',')
    },
    {
      id: 'action-create-dashboard',
      label: L.admin_panel.command_palette.actions.create_dashboard.label,
      description: L.admin_panel.command_palette.actions.create_dashboard.description,
      href: '/admin-panel/dashboards/new',
      iconName: 'PlusIcon',
      category: 'actions',
      priority: 8,
      searchTerms: L.admin_panel.command_palette.search_terms.actions.create_dashboard.split(',')
    },
    {
      id: 'action-manage-reminders',
      label: L.admin_panel.command_palette.actions.manage_reminders.label,
      description: L.admin_panel.command_palette.actions.manage_reminders.description,
      href: '/admin-panel/accounts?action=manage-reminders',
      iconName: 'CalendarIcon',
      category: 'actions',
      priority: 4,
      searchTerms: L.admin_panel.command_palette.search_terms.actions.manage_reminders.split(',')
    },
    {
      id: 'action-toggle-sidebar',
      label: L.admin_panel.command_palette.actions.toggle_sidebar.label,
      description: L.admin_panel.command_palette.actions.toggle_sidebar.description,
      iconName: 'Bars3Icon',
      category: 'actions',
      priority: 3,
      searchTerms: L.admin_panel.command_palette.search_terms.actions.toggle_sidebar.split(',')
    },
    {
      id: 'action-logout',
      label: L.menu.logout,
      description: L.admin_panel.command_palette.actions.logout.description,
      iconName: 'ArrowRightOnRectangleIcon',
      category: 'actions',
      priority: 2,
      searchTerms: L.admin_panel.command_palette.search_terms.actions.logout.split(',')
    },
    // Dynamic language switching actions
    ...languages.map((lang) => ({
      id: `action-language-${lang}`,
      label: `${L.admin_panel.command_palette.actions.change_language.label}: ${langToWord(lang, 'Name')}`,
      description: `${L.admin_panel.command_palette.actions.change_language.description} - ${langToWord(lang, 'Name')}`,
      iconName: 'LanguageIcon' as IconName,
      category: 'actions' as const,
      priority: 0, // Low priority so they appear at bottom
      searchTerms: [
        ...L.admin_panel.command_palette.search_terms.actions.change_language.split(','),
        langToWord(lang, 'Name')?.toLowerCase() || '',
        lang
      ]
    }))
  ], [L, languages]);

  // Filter and sort commands based on search
  const { filteredCommands, commandScores } = useMemo(() => {
    const searchLower = search.toLowerCase().trim();
    
    if (!searchLower) {
      // Show only high priority items when no search
      const commands = allCommands
        .filter(cmd => (cmd.priority || 0) >= 5)
        .sort((a, b) => (b.priority || 0) - (a.priority || 0));
      return { filteredCommands: commands, commandScores: new Map() };
    }
    
    // When searching, show all matching commands with fuzzy search
    const commandsWithScores = allCommands
      .map(cmd => {
        const labelMatch = cmd.label?.toLowerCase().includes(searchLower);
        const searchTermsMatch = cmd.searchTerms?.some(term => 
          term.toLowerCase().includes(searchLower)
        );
        
        // Use fuzzy search for descriptions
        const descriptionFuzzy = cmd.description 
          ? fuzzyMatch(cmd.description, searchLower) 
          : { isMatch: false, score: 0 };
        
        const isMatch = labelMatch || searchTermsMatch || descriptionFuzzy.isMatch;
        
        // Calculate combined score for sorting
        let score = 0;
        if (labelMatch) {
          // Prioritize label matches
          score += cmd.label.toLowerCase().startsWith(searchLower) ? 100 : 80;
        }
        if (searchTermsMatch) {
          score += 60;
        }
        if (descriptionFuzzy.isMatch) {
          score += descriptionFuzzy.score * 0.5; // Weight description matches lower
        }
        
        // Add priority bonus
        score += (cmd.priority || 0) * 2;
        
        return { cmd, score, isMatch };
      })
      .filter(item => item.isMatch)
      .sort((a, b) => b.score - a.score);
    
    const commands = commandsWithScores.map(item => item.cmd);
    const scores = new Map(commandsWithScores.map(item => [item.cmd.id, item.score]));
    
    return { filteredCommands: commands, commandScores: scores };
  }, [search, allCommands]);

  // Group commands by category
  const groupedCommands = useMemo(() => {
    const groups: Record<string, CommandAction[]> = {};
    
    filteredCommands.forEach(cmd => {
      if (!groups[cmd.category]) {
        groups[cmd.category] = [];
      }
      groups[cmd.category].push(cmd);
    });
    
    return groups;
  }, [filteredCommands]);

  // Handle command execution
  const executeCommand = (command: CommandAction) => {
    onClose();
    
    switch (command.id) {
      case 'action-logout':
        window.location.assign('/logout');
        break;
      
      case 'action-toggle-sidebar':
        toggleSidebarNav();
        break;
      
      default:
        // Handle language switching actions
        if (command.id.startsWith('action-language-')) {
          const langCode = command.id.replace('action-language-', '');
          interfaceContext.setLanguage(langCode as any);
          break;
        }
        
        // Handle regular navigation
        if (command.href) {
          if (command.isExternal) {
            window.open(command.href, '_blank', 'noopener,noreferrer');
          } else {
            history.push(command.href);
          }
        }
        break;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'navigation':
        return L.admin_panel.command_palette.categories.navigation;
      case 'actions':
        return L.admin_panel.command_palette.categories.actions;
      case 'recent':
        return L.admin_panel.command_palette.categories.recent;
      default:
        return category;
    }
  };

  const renderCommandGroups = () => {
    // When searching, order categories by their highest-scoring result
    // When not searching, use default order
    let categoryOrder = ['navigation', 'actions', 'recent'];
    
    if (search) {
      // Calculate max score for each category
      const categoryScores = Object.entries(groupedCommands).map(([category, commands]) => {
        if (!commands || commands.length === 0) return { category, maxScore: -1 };
        
        // Find the highest score in this category using commandScores map
        const maxScore = Math.max(...commands.map(cmd => commandScores.get(cmd.id) || 0));
        
        return { category, maxScore };
      });
      
      // Sort categories by max score (highest first)
      categoryOrder = categoryScores
        .filter(cs => cs.maxScore >= 0)
        .sort((a, b) => b.maxScore - a.maxScore)
        .map(cs => cs.category);
    }
    
    return categoryOrder.map(category => {
      const commands = groupedCommands[category];
      if (!commands || commands.length === 0) return null;
      
      return (
        <CommandGroup key={category} heading={getCategoryLabel(category)}>
          {commands.map(command => (
            <CommandItem
              key={command.id}
              value={`${command.label} ${command.description || ''} ${command.searchTerms?.join(' ') || ''}`}
              onSelect={() => executeCommand(command)}
              className="flex items-start gap-2 px-2 py-1.5"
            >
              {command.iconName && (
                <Icon 
                  name={command.iconName} 
                  className="h-4 w-4 shrink-0 text-gray-600 mt-[2px]" 
                  role="decorative"
                  type="solid"
                />
              )}
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-sm font-medium truncate">
                  {command.label}
                </span>
                {search && command.description && (
                  <span className="text-xs text-gray-600 truncate">
                    {command.description}
                  </span>
                )}
              </div>
              {command.shortcut && (
                <CommandShortcut>{command.shortcut}</CommandShortcut>
              )}
            </CommandItem>
          ))}
        </CommandGroup>
      );
    });
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <div className="fixed inset-0 flex items-start justify-center pt-[20vh]">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
            afterLeave={() => setSearch('')}
          >
            <Dialog.Panel className="bg-white rounded-lg shadow-2xl ring-1 ring-black/10 overflow-hidden w-full max-w-2xl mx-4">
              <Dialog.Title className="sr-only">
                {L.admin_panel.command_palette.title}
              </Dialog.Title>
              <Command>
                <CommandInput 
                  placeholder={L.admin_panel.command_palette.placeholder} 
                  className="h-12 text-base border-none focus:ring-0"
                  value={search}
                  onValueChange={setSearch}
                />
                <CommandList className="max-h-96">
                  <CommandEmpty>
                    <div className="py-6 text-center text-sm text-gray-700">
                      <Icon name="MagnifyingGlassIcon" className="mx-auto h-6 w-6 mb-2 opacity-50" type="solid" />
                      {L.admin_panel.command_palette.no_commands_found}
                    </div>
                  </CommandEmpty>
                  {renderCommandGroups()}
                </CommandList>
              </Command>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};